import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { newsroomDetailState } from "@/lib/newsroomNavigation";
import { api } from "@/lib/api";
import { stripHtml } from "@/lib/html";
import type { TodayItem, NewsItem, CommunityPost } from "@/lib/types";
import ScrollReveal from "@/components/base/ScrollReveal";

type CategoryFilter = "All" | "Biocon Today" | "Biocon News" | "Community";

type UnifiedPost = {
  id: number;
  sourceType: "today" | "news" | "community";
  category: CategoryFilter;
  title: string;
  author: string;
  date: string;
  views: number;
  image_url: string | null;
  link: string | null;
  content: string | null;
  communityCategory?: string;
};

const categoryBadge = (cat: CategoryFilter | string) => {
  const map: Record<string, string> = {
    "Biocon Today": "bg-emerald-50 text-emerald-600",
    "Biocon News": "bg-sky-50 text-sky-600",
    "Community": "bg-amber-50 text-amber-700",
    "General": "bg-gray-100 text-gray-600",
    "Notice": "bg-amber-50 text-amber-600",
    "Event": "bg-sky-50 text-sky-600",
    "Research": "bg-emerald-50 text-emerald-600",
    "Partnership": "bg-indigo-50 text-indigo-600",
    "Recruit": "bg-rose-50 text-rose-600",
  };
  return map[cat] || "bg-gray-100 text-gray-600";
};

const mainCategories: CategoryFilter[] = ["All", "Biocon Today", "Biocon News", "Community"];

export default function NewsroomPage() {
  const location = useLocation();
  const listReturnTo = `${location.pathname}${location.search}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initCategory: CategoryFilter =
    tabParam === "today" ? "Biocon Today"
    : tabParam === "news" ? "Biocon News"
    : tabParam === "community" ? "Community"
    : "All";

  const [activeCategory, setActiveCategory] = useState<CategoryFilter>(initCategory);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const [allPosts, setAllPosts] = useState<UnifiedPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, newsRes, communityRes] = await Promise.all([
        api.get<TodayItem[]>("/public/news/today"),
        api.get<NewsItem[]>("/public/news/news"),
        api.get<CommunityPost[]>("/public/news/community"),
      ]);

      const todayPosts: UnifiedPost[] = (todayRes.data || []).map((item: TodayItem) => ({
        id: item.id,
        sourceType: "today",
        category: "Biocon Today",
        title: item.title,
        author: "BIOCON",
        date: item.date,
        views: 0,
        image_url: item.imageUrl,
        link: item.link,
        content: null,
        communityCategory: undefined,
      }));

      const newsPosts: UnifiedPost[] = (newsRes.data || []).map((item: NewsItem) => ({
        id: item.id,
        sourceType: "news",
        category: "Biocon News",
        title: item.title,
        author: item.author || "BIOCON",
        date: item.date,
        views: 0,
        image_url: item.imageUrl,
        link: item.link,
        content: item.excerpt,
        communityCategory: undefined,
      }));

      const communityPosts: UnifiedPost[] = (communityRes.data || []).map((item: CommunityPost) => ({
        id: item.id,
        sourceType: "community",
        category: "Community",
        title: item.title,
        author: item.author || "BIOCON",
        date: item.date,
        views: item.views,
        image_url: item.imageUrl,
        link: item.link,
        content: item.content,
        communityCategory: item.category,
      }));

      const merged = [...todayPosts, ...newsPosts, ...communityPosts].sort((a, b) =>
        b.date.localeCompare(a.date)
      );
      setAllPosts(merged);
    } catch {
      // 로딩 실패 시 빈 목록 유지
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // URL param -> category sync
  useEffect(() => {
    if (tabParam === "today") setActiveCategory("Biocon Today");
    else if (tabParam === "news") setActiveCategory("Biocon News");
    else if (tabParam === "community") setActiveCategory("Community");
  }, [tabParam]);

  const handleCategoryChange = (cat: CategoryFilter) => {
    setActiveCategory(cat);
    setSearch("");
    setPage(1);
    if (cat === "Biocon Today") setSearchParams({ tab: "today" });
    else if (cat === "Biocon News") setSearchParams({ tab: "news" });
    else if (cat === "Community") setSearchParams({ tab: "community" });
    else setSearchParams({});
  };

  const filtered = allPosts.filter((post) => {
    if (activeCategory !== "All" && post.category !== activeCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      const plain = post.content ? stripHtml(post.content).toLowerCase() : "";
      return post.title.toLowerCase().includes(q) || plain.includes(q);
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getRowLink = (post: UnifiedPost) => `/newsroom/${post.sourceType}/${post.id}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#f1f7fc] pt-28 pb-12">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1.5 h-6 bg-[#1977cc] rounded-full" />
              <h1 className="text-2xl md:text-3xl font-bold text-[#2c4964]">Newsroom</h1>
            </div>
            <p className="text-gray-500 text-sm md:text-base max-w-2xl">
              바이오컨의 최신 소식, 뉴스, 공지사항 및 커뮤니티를 한 곳에서 확인하세요.
            </p>
          </ScrollReveal>
        </div>
      </div>

      {/* Filters */}
      <div className="container mx-auto px-4 mt-8 mb-6">
        <div className="flex flex-col gap-4">
          {/* Main category filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {mainCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? "bg-[#1977cc] text-white"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {cat}
                {cat !== "All" && (
                  <span className="ml-1.5 text-xs opacity-70">
                    ({allPosts.filter((p) => p.category === cat).length})
                  </span>
                )}
              </button>
            ))}
          </div>



          {/* Search bar */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100 max-w-md">
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Search newsroom..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="flex-1 text-sm text-[#2c4964] placeholder-gray-400 outline-none bg-transparent"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Board Table */}
      <div className="container mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#1977cc] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap w-[140px]">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap w-[120px]">Author</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap w-[120px]">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap w-[80px]">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((post) => {
                    const internalLink = getRowLink(post);

                    return (
                      <tr
                        key={`${post.sourceType}-${post.id}`}
                        className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors duration-150"
                      >
                        {/* Category */}
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap w-fit ${categoryBadge(post.category)}`}>
                            {post.category}
                          </span>
                        </td>

                        {/* Title */}
                        <td className="px-4 py-3.5">
                          <Link to={getRowLink(post)} state={newsroomDetailState(listReturnTo)} className="group block">
                            <p className="text-sm font-medium text-[#2c4964] group-hover:text-[#1977cc] transition-colors line-clamp-1">
                              {post.title}
                            </p>
                            {post.content && (
                              <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{stripHtml(post.content)}</p>
                            )}
                          </Link>
                        </td>

                        {/* Author */}
                        <td className="px-4 py-3.5 text-gray-500 text-xs whitespace-nowrap">{post.author}</td>

                        {/* Date */}
                        <td className="px-4 py-3.5 text-gray-500 text-xs whitespace-nowrap">{post.date}</td>

                        {/* Views */}
                        <td className="px-4 py-3.5 text-right text-gray-500 text-xs whitespace-nowrap">
                          {post.views > 0 ? post.views.toLocaleString() : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-50 text-gray-300">
                  <i className="ri-search-line text-2xl" />
                </div>
                <p className="text-gray-400 text-sm">No items found.</p>
              </div>
            )}

            {/* Pagination */}
            {filtered.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <p className="text-xs text-gray-400">{filtered.length} items total</p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    const start = Math.max(1, Math.min(currentPage - 3, totalPages - 6));
                    return start + i;
                  }).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium transition-colors cursor-pointer ${
                        p === currentPage
                          ? "bg-[#1977cc] text-white"
                          : "text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}